import { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { parseJwtExpiration, useAuthStore } from '@/store/useAuthStore';
import { loescheAlleSupportEntwuerfe } from '@/components/admin/support/useEntwurf';
import { SessionExpiredDialog } from './SessionExpiredDialog';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Determine initial auth state on mount.
 * Returns { authenticated, tokenWasExpired }
 */
function checkInitialAuth(): { authenticated: boolean; tokenWasExpired: boolean } {
  const token = api.getToken();
  const rawToken = localStorage.getItem('auth_token');

  // Debug: log auth state on mount (helps diagnose refresh-logout issues)
  console.log('[AuthGuard] init:', {
    hasToken: !!token,
    hasRawToken: !!rawToken,
    tokensMatch: token === rawToken,
    tokenLength: token?.length ?? 0,
  });

  if (!token) {
    console.log('[AuthGuard] No token found — redirecting to login');
    return { authenticated: false, tokenWasExpired: false };
  }

  // Check if token is expired
  const expiresAt = parseJwtExpiration(token);
  const now = Date.now();
  console.log('[AuthGuard] Token expiry:', {
    expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    now: new Date(now).toISOString(),
    remainingMs: expiresAt ? expiresAt - now : null,
    isExpired: !expiresAt || now > expiresAt,
  });

  if (!expiresAt || now > expiresAt) {
    api.logout();
    return { authenticated: false, tokenWasExpired: true };
  }

  return { authenticated: true, tokenWasExpired: false };
}

export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clear);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState<string | undefined>(undefined);
  const sessionExpiredShown = useRef(false);

  // Run once on mount — determine if we're authenticated or if session expired
  const [initialCheck] = useState(checkInitialAuth);
  const [isAuthenticated, setIsAuthenticated] = useState(initialCheck.authenticated);

  // Token beim Laden schon abgelaufen: aufräumen. Angezeigt wird hier nichts — der erste Render
  // leitet über <Navigate> unten zur Anmeldung, bevor ein Dialog erscheinen könnte.
  // V-0155: Hier stand zusätzlich `setShowSessionExpired(true)` (react-hooks/set-state-in-effect).
  // Es war wirkungslos: Das Navigate im selben Commit hatte die Seite schon verlassen.
  useEffect(() => {
    if (initialCheck.tokenWasExpired && !sessionExpiredShown.current) {
      sessionExpiredShown.current = true;
      // Finding 8: ein erzwungenes Abmelden (hier: Token beim Laden schon abgelaufen) räumt auch
      // ungesendete Support-Entwürfe dieses Browsers weg — sonst sieht der nächste Login auf
      // demselben Gerät (anderer Superadmin) fremde Antwortentwürfe.
      loescheAlleSupportEntwuerfe();
      clearAuth();
    }
  }, [initialCheck.tokenWasExpired, clearAuth]);

  const handleLogin = useCallback(() => {
    setShowSessionExpired(false);
    sessionExpiredShown.current = false;
    const loginPath = location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
    navigate(loginPath, { state: { from: location }, replace: true });
  }, [location, navigate]);

  // Listen for 401 responses from API calls (e.g. token invalidated server-side)
  useEffect(() => {
    const handleUnauthorized = (e: Event) => {
      if (sessionExpiredShown.current) return;
      sessionExpiredShown.current = true;

      const reason = (e as CustomEvent<{ reason?: string }>).detail?.reason;
      setSessionExpiredReason(reason);
      // Finding 8: erzwungener 401-Abmelde-Pfad — dieselbe Aufräumung wie beim manuellen
      // Abmelden (AdminLayout.tsx) und beim abgelaufenen Token oben.
      loescheAlleSupportEntwuerfe();
      api.logout();
      clearAuth();
      setIsAuthenticated(false);
      setShowSessionExpired(true);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [clearAuth]);

  if (!isAuthenticated && !showSessionExpired) {
    const loginPath = location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  return (
    <>
      {children}
      {showSessionExpired && <SessionExpiredDialog onLogin={handleLogin} reason={sessionExpiredReason} />}
    </>
  );
}
